<template>
  <TransactionListTemplate
    :screen-config="setScreenConfig"
    :row-click="rowClickCallBack"
    persistence-key="timesheet-list"
    :setLoading="setLoading"
    ref="timesheet-list"
  >
    <template #popup>
      <timesheet-modal
        :form-mode="popup.formMode"
        :timesheet-code="popup.timesheetCode"
        :is-visible.sync="popup.isPopupShow"
        @saved="onTimesheetSaved"
      />
    </template>
  </TransactionListTemplate>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";

import TransactionListTemplate from "@/templates/transaction-list";
import TimesheetModal from "./timesheet-modal.vue";
import { FeatureStorage } from "@/_services/storage";
import ColumnWidth from "@/_helpers/data-grid/ColumnWidth";
import { authComputed } from "@/_store/helpers";

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    TransactionListTemplate,
    TimesheetModal,
  },
  data() {
    return {
      setLoading: false,
      popup: {
        title: "",
        isPopupShow: false,
        formMode: "create",
        timesheetCode: 0,
      },
    };
  },
  computed: {
    ...authComputed,
    setScreenConfig() {
      return {
        title: "Timesheet",
        ScreenName: "Timesheet",
        exportFileName: "Timesheet List",
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "GraycorpHelpdeskTimesheet",
              serverPaging: true,
              allowGrouping: false,
              select: [
                "TimesheetCode",
                "DateWorked",
                "LabourResourceName",
                "LabourResourceCode",
                "HoursWorked",
                "HoursBilled",
                "DateCreated",
                "CreatedByName",
                "CreatedBy",
                "Status",
                "WorkItemList",
                "ProjectList",
                "ProcessList",
              ],
            },
            noDataMessage: {
              addNew: {
                class: "add-new",
                title: "No Timesheets",
                message: "Get Started, Add a Timesheet",
              },
            },
          },
          toolbar: {
            screenName: "Timesheet",
            search: false,
            columnChooser: true,
            export: true,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                clickAction: this.onNewTimesheetHandler,
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission("Timesheet", "create"),
              },
            ],
          },
          columns: [
            {
              fieldName: "TimesheetCode",
              dataField: "TimesheetCode",
              gridDataField: "TimesheetCode",
              filterDataField: "TimesheetCode",
              label: "Timesheet",
              filterLabel: "Timesheet",
              type: "Integer",
              allowHiding: false,
              forceWidth: true,
              filterIndex: 0,
              sortOrder: "desc",
              alignment: "left",
              width: 100,
              minWidth: 100,
            },
            {
              fieldName: "DateWorked",
              dataField: "DateWorked",
              filterDataField: "DateWorked",
              label: "Date Worked",
              filterLabel: "Date Worked",
              type: "Date",
              filterIndex: 1,
              width: ColumnWidth.Date.width,
              minWidth: ColumnWidth.Date.minWidth,
              editorOptions: { max: new Date() },
            },
            {
              fieldName: "LabourResourceName",
              dataField: "LabourResourceName",
              filterDataField: "LabourResourceCode",
              label: "Team Member",
              filterLabel: "Team Member",
              filterIndex: 2,
              cellTemplate: "Record",  
              cellTemplateRecord: {
                  primary: [
                    {
                        dataField: "LabourResourceName"
                    }
                  ],
                  secondary: [
                    {
                        dataField: "LabourResourceCode"
                    }
                  ]
                },
              dataSourceConfig: {
                endpoint: "api/LabourResource",
                sort: [{ selector: "FirstName", asc: true }],
                filter: [["Inactive", "=", "N"]],
              },
              valueExpr: "Code",
              displayExpr: "FullName",
              editorTemplate: {
                primary: [
                  {
                    dataField: "FullName",
                  },
                ],
                secondary: [
                  {
                    dataField: "Code",
                  },
                ],
              },
            },
            {
              fieldName: "WorkItemList",
              filterDataField: "WorkItemList",
              filterLabel: "Work Item",
              type: "Text",
              filterEditorType: "dxTextBox",
              filterOperator: "contains",
              filterIndex: 3,
              showInColumnChooser: false,
              visible: false,
            },
            {
              fieldName: "ProjectList",
              filterDataField: "ProjectList",
              filterLabel: "Project",
              filterDataRelationship: "Comma",
              filterOperator: "contains",
              filterIndex: 4,
              dataSourceConfig: {
                endpoint: "api/GraycorpHelpdeskTimesheetProjectSearch",
                sort: [{ selector: "ProjectCode", asc: false }],
              },
              valueExpr: "ProjectCode",
              displayExpr: "ProjectDescription",
              searchExpr: ["ProjectSearchText"],
              editorTemplate: {
                primary: [
                  {
                    dataField: "ProjectDescription",
                  },
                ],
                secondary: [
                  {
                    dataField: "ProjectCode",
                  },
                ],
              },
              showInColumnChooser: false,
              visible: false,
            },
            {
              fieldName: "ProcessList",
              filterDataField: "ProcessList",
              filterLabel: "Process",
              filterDataRelationship: "Comma",
              filterOperator: "contains",
              filterIndex: 5,
              dataSourceConfig: {
                endpoint: "api/Process",
                sort: [{ selector: "Description", asc: true }],
                filter: [["Inactive", "=", "N"]],
              },
              valueExpr: "Code",
              displayExpr: "Description",
              editorTemplate: {
                primary: [
                  {
                    dataField: "Description",
                  },
                ],
                secondary: [
                  {
                    dataField: "Code",
                  },
                ],
              },
              showInColumnChooser: false,
              visible: false,
            },
            {
              fieldName: "HoursWorked",
              dataField: "HoursWorked",
              label: "Hours Worked",
              type: "Decimal",
              format: "Decimal-16-2",
              width: ColumnWidth.Quantity2DecimalPoints.width,
            },
            {
              fieldName: "HoursBilled",
              dataField: "HoursBilled",
              label: "Hours Billed",
              type: "Decimal",
              format: "Decimal-16-2",
              width: ColumnWidth.Quantity2DecimalPoints.width,
            },
            {
              fieldName: "DateCreated",
              dataField: "DateCreated",
              filterDataField: "DateCreated",
              label: "Date Created",
              filterLabel: "Date Created",
              type: "Date",
              filterIndex: 6,
              width: ColumnWidth.DateCreated.width,
            },
            {
              screenField: "CreatedBy",
              dataField: "CreatedBy",
              gridLabel: "Created By",
              filterLabel: "Created By",
              filterIndex: 7,
              dataSourceConfig: {
                endpoint: "api/Users",
                sort: {
                  "selector": "Username",
                  "asc": true
                },
              },
            },
            {
              fieldName: "Status",
              dataField: "Status",
              label: "Status",
              cellTemplate: "Status",
              dataSourceConfig: {
                data: [{ key: "Completed", value: "Completed" }],
              },
              valueExpr: "key",
              displayExpr: "value",
              width: ColumnWidth.Status.width,
            },
          ],
        },
      };
    },
  },
  methods: {
    showPopupModal() {
      this.popup.isPopupShow = true;
    },
    onNewTimesheetHandler() {
      this.popup.formMode = "create";
      this.popup.timesheetCode = 0;
      this.showPopupModal();
    },
    async rowClickCallBack(e) {
      if (e.rowType === "data" && e.column.type !== "selection") {
        this.popup.formMode = "review";
        this.popup.timesheetCode = e.data.TimesheetCode;
        this.showPopupModal();
      }
    },
    onTimesheetSaved() {
      const grid =
        this.$refs["timesheet-list"] &&
        this.$refs["timesheet-list"].$refs["transactionListGrid"];
      if (grid && grid.gridRefresh) grid.gridRefresh();
    },
  },
};
</script>

<style lang="scss"></style>
